import { useCallback, useEffect, useState } from "react";

import type { PortalOverview } from "@bigbear/shared";

import type { PortalApi } from "./api/portal";

interface State {
  data: PortalOverview | null;
  error: string | null;
  loading: boolean;
  /**
   * Re-fetch without waiting for `api` to change identity. Removing a machine
   * changes what the *next* call to `overview()` returns, not what the last
   * one did, so the page needs a way to ask for that next call on demand.
   */
  reload: () => void;
}

/** Load the overview once, and report failure rather than rendering nothing. */
export function useOverview(api: PortalApi): State {
  const [state, setState] = useState<Omit<State, "reload">>({
    data: null,
    error: null,
    loading: true,
  });
  // A dependency `useEffect` can react to without the caller passing a new
  // `api` instance: incrementing this is the whole implementation of reload.
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    let active = true;
    setState((previous) => ({ ...previous, loading: true }));

    api
      .overview()
      .then((data) => {
        if (active) setState({ data, error: null, loading: false });
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setState({
          data: null,
          error: cause instanceof Error ? cause.message : "The portal could not load your backups.",
          loading: false,
        });
      });

    // A response arriving after unmount would otherwise set state on a dead
    // component.
    return () => {
      active = false;
    };
  }, [api, generation]);

  const reload = useCallback(() => setGeneration((g) => g + 1), []);

  return { ...state, reload };
}
